/**
 * Generic 4-step "connection sequence" state machine for the PRIMARY session connection
 * (config → signaling/WebRTC join → robot config → video). Reconnect/recovery
 * (`WS_RECONNECTING`, ICE restart, `rejoinRoom`) is out of scope — it stays owned by
 * `teleop-session`'s own status codes and is not modeled here.
 *
 * This file deliberately knows NOTHING about any specific step's domain — no
 * `SessionPhase`, no `RobotConfigStatus`, not even `config.json` loading. It only knows
 * the generic shape every step shares: has it resolved yet, and if so, did it fail. Each
 * step's real classification (e.g. "which `SessionPhase` values count as the connect
 * step being done") is the CALLER's concern — see `useConnectionGate.ts`'s
 * `*StepOutcome()` functions, which translate each step's real, app-specific status into
 * the `StepOutcome` vocabulary below before calling in here. That boundary mirrors the
 * one `teleop-state/session.ts` already draws around `teleop-session` via its own
 * `SessionLike` interface, for the same reason: a framework-free/pure module should
 * not import another layer's types just to read one field off it.
 */

export type ConnectionStep = "config" | "connect" | "robotConfig" | "video";

/** Canonical order the 4 steps run in — the only place this sequence is spelled out. */
export const CONNECTION_STEPS: readonly ConnectionStep[] = [
  "config",
  "connect",
  "robotConfig",
  "video",
];

/** A single step's own outcome — the entire vocabulary this module knows about. */
export type StepOutcome = "pending" | "done" | "error";

export type ConnectionState =
  | { kind: "pending"; step: ConnectionStep }
  | { kind: "active" }
  | { kind: "failed"; step: ConnectionStep };

/** One already-classified outcome per step, in no particular order (this module imposes
 * the order via `CONNECTION_STEPS`). */
export type ConnectionStepOutcomes = Record<ConnectionStep, StepOutcome>;

/**
 * Sequence the 4 independently-classified step outcomes into one connection state.
 * Walks `CONNECTION_STEPS` in order and stops at the first step that isn't "done" — that
 * step is the current step, and if its outcome is "error" the whole sequence is failed.
 * Everything before it is implicitly done (by construction: this function would have
 * already stopped there otherwise); everything after it is not evaluated at all, which is
 * what keeps a not-yet-reached step's transient outcome from ever being observed as
 * "current" — e.g. robot-config briefly reporting something before signaling has even
 * connected never surfaces, because this function never gets that far.
 */
export function deriveConnectionState(outcomes: ConnectionStepOutcomes): ConnectionState {
  for (const step of CONNECTION_STEPS) {
    const outcome = outcomes[step];
    if (outcome === "error") return { kind: "failed", step };
    if (outcome === "pending") return { kind: "pending", step };
  }
  return { kind: "active" };
}
